import { gql } from '@apollo/client';
import {
  Button,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
  useToast,
} from '@chakra-ui/react';
import { t } from 'i18next';
import { useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '~auth/useAuth';
import { ModalCloseButton } from '~components/ui/ModalCloseButton';
import { getId } from '~utils/data';
import { fromError } from '~utils/errors';
import { ensure } from '~utils/types';
import {
  DeleteSchedulesModal_ScheduleFragment,
  useDeleteSchedulesMutation,
} from './__generated__/DeleteSchedulesModal.graphql';

interface Props {
  schedules: DeleteSchedulesModal_ScheduleFragment[];
  isOpen: boolean;
  onCancel: () => void;
  onSuccess?: () => Promise<void> | void;
}

export function DeleteSchedulesModal({ schedules, isOpen, onCancel, onSuccess }: Props) {
  const isUsedByDisplays = schedules.some((schedule) => schedule.allDisplays.length > 0);
  const isUsedByConfigurations = schedules.some(
    (schedule) => schedule.isScheduleUsedByAnyConfiguration,
  );
  const toast = useToast();
  const [deleteSchedules] = useDeleteSchedulesMutation();
  const auth = useAuth();
  const navigate = useNavigate();
  const { customer } = useParams();

  const handleGoToConfiguration = useCallback(() => {
    onCancel();
    const customerHandle = ensure(customer);
    navigate(`/${auth.organization?.handle}/customers/${customerHandle}/configurations`);
  }, [onCancel, navigate, auth.organization?.handle, customer]);

  const handleDelete = useCallback(async () => {
    try {
      await deleteSchedules({
        variables: { input: { scheduleIds: schedules.map(getId) } },
      });

      toast({
        status: 'success',
        title: schedules.length === 1 ? t('scheduleDeleted') : t('schedulesDeleted'),
        description: schedules.length === 1 ? t('scheduleDeletedDesc') : t('scheduleDeletedDesc1'),
      });

      await onSuccess?.();
    } catch (err) {
      toast({
        status: 'error',
        title: t('schedulesDeleteErr'),
        description: fromError(err, 'DeleteSchedules', {
          SCHEDULE_NOT_FOUND: t('schedulesDeleteErrDesc'),
          SCHEDULE_IN_USE: t('schedulesDeleteErrDesc1'),
        }),
      });
    }
  }, [deleteSchedules, onSuccess, schedules, toast]);

  const isUsedByManySchedules = schedules.length > 1;

  return (
    <Modal isOpen={isOpen} onClose={onCancel} scrollBehavior="inside">
      <ModalOverlay />

      {isUsedByDisplays || isUsedByConfigurations ? (
        <ModalContent>
          <ModalHeader>
            {isUsedByManySchedules ? t('schedulesDeleteErr1') : t('schedulesDeleteErr2')}
          </ModalHeader>
          <ModalCloseButton tabIndex={5} />
          <ModalBody>
            <Text>
              {isUsedByConfigurations && isUsedByDisplays
                ? t(
                    isUsedByManySchedules
                      ? 'scheduleInUseInConfigAndDisplaysError'
                      : 'scheduleInUseInConfigAndDisplaysErrorSingle',
                  )
                : isUsedByConfigurations
                ? t(
                    isUsedByManySchedules
                      ? 'scheduleDeletionConfigErrorMany'
                      : 'scheduleDeletionConfigErrorSingle',
                  )
                : t(
                    isUsedByManySchedules
                      ? 'scheduleDeletionErrorMany'
                      : 'scheduleDeletionErrorSingle',
                  )}
            </Text>
          </ModalBody>
          <ModalFooter>
            {isUsedByDisplays ? (
              <Button onClick={onCancel} variant="solid" colorScheme="blue">
                {t('ok')}
              </Button>
            ) : (
              <>
                <Button onClick={onCancel} variant="ghost" colorScheme="blue">
                  {t('cancel')}
                </Button>
                <Button
                  onClick={handleGoToConfiguration}
                  variant="solid"
                  colorScheme="blue"
                  marginLeft="3"
                >
                  {t('goToConfigurations')}
                </Button>
              </>
            )}
          </ModalFooter>
        </ModalContent>
      ) : (
        <ModalContent>
          <ModalHeader>
            {schedules.length > 1 ? t('deleteSchedules') : t('deleteSchedule')}
          </ModalHeader>
          <ModalCloseButton tabIndex={5} />
          <ModalBody>
            <Text>{t('deleteScheduleConfirmation', { count: schedules.length })}</Text>
            <Text color={'gray.600'} marginTop="6">
              {t('actionCannotBeUndone')}
            </Text>
          </ModalBody>
          <ModalFooter>
            <Button onClick={onCancel} variant="ghost" colorScheme="blue">
              {t('cancel')}
            </Button>
            <Button variant="solid" colorScheme="red" marginLeft="3" onClick={handleDelete}>
              {t('delete')}
            </Button>
          </ModalFooter>
        </ModalContent>
      )}
    </Modal>
  );
}

DeleteSchedulesModal.graphql = {
  fragments: {
    DeleteSchedulesModal_schedule: gql`
      fragment DeleteSchedulesModal_schedule on Schedule {
        id
        isScheduleUsedByAnyConfiguration
        allDisplays: displays {
          id
        }
      }
    `,
  },
  mutations: {
    DeleteSchedules: gql`
      mutation DeleteSchedules($input: SchedulesDeleteInput!) {
        schedulesDelete(input: $input) {
          scheduleIds
        }
      }
    `,
  },
};
